import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Grid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Center,
  Text,
  useToast,
  HStack,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  CardHeader,
} from '@chakra-ui/react';
import { FiSearch, FiMapPin, FiTrendingUp, FiUsers, FiMap } from 'react-icons/fi';
import {
  getAllWaitingListEntries,
  getWaitingListMetrics,
  WaitingListEntry,
  WaitingListMetrics,
} from '../services/waitingListMetrics';
import WaitingListStaticMap from './WaitingList/WaitingListStaticMap';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Cast icons as React.ElementType to fix TypeScript issues
const FiSearchIcon = FiSearch as React.ElementType;
const FiUsersIcon = FiUsers as React.ElementType;
const FiMapIcon = FiMap as React.ElementType;

const WaitingListManagement: React.FC = () => {
  const [entries, setEntries] = useState<WaitingListEntry[]>([]);
  const [metrics, setMetrics] = useState<WaitingListMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState<WaitingListEntry | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [showClusters, setShowClusters] = useState(true);
  const toast = useToast();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [entriesData, metricsData] = await Promise.all([
          getAllWaitingListEntries(),
          getWaitingListMetrics(),
        ]);
        setEntries(entriesData);
        setMetrics(metricsData);
      } catch (error) {
        console.error('Error fetching waiting list data:', error);
        toast({
          title: 'Error loading waiting list',
          description: 'Failed to fetch waiting list data',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [toast]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const formatAddress = (entry: WaitingListEntry) => {
    const parts = [];
    if (entry.street) parts.push(entry.street);
    if (entry.city) parts.push(entry.city);
    if (entry.state) parts.push(entry.state);
    if (entry.zipCode) parts.push(entry.zipCode);
    return parts.length > 0 ? parts.join(', ') : 'Address not available';
  };

  // Filter entries
  const filteredEntries = entries.filter((entry) => {
    const matchesSearch =
      !searchTerm ||
      formatAddress(entry).toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.reason?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesState = !stateFilter || entry.state === stateFilter;

    return matchesSearch && matchesState;
  });

  // Get unique states for filter
  const uniqueStates = Array.from(new Set(entries.map(e => e.state).filter(Boolean))).sort();

  // Prepare chart data
  const stateDistribution = metrics
    ? Object.entries(metrics.entriesByState)
        .map(([state, count]) => ({ name: state, value: count }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10)
    : [];

  if (loading || !metrics) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box>
      <Heading
        size={{ base: 'md', md: 'lg' }}
        mb={6}
        pl={{ base: '60px', lg: 0 }}
        pr={{ base: 4, lg: 0 }}
      >
        Waiting List Management
      </Heading>

      {/* Key Metrics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Total Entries</StatLabel>
          <StatNumber>{metrics.totalEntries}</StatNumber>
          <StatHelpText>All waiting list entries</StatHelpText>
        </Stat>

        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Geographic Clusters</StatLabel>
          <StatNumber>{metrics.geographicClusters.length}</StatNumber>
          <StatHelpText>Potential service areas</StatHelpText>
        </Stat>

        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Top State</StatLabel>
          <StatNumber fontSize="lg">
            {Object.entries(metrics.entriesByState)
              .sort(([, a], [, b]) => (b as number) - (a as number))[0]?.[0] || 'N/A'}
          </StatNumber>
          <StatHelpText>
            {Object.entries(metrics.entriesByState)
              .sort(([, a], [, b]) => (b as number) - (a as number))[0]?.[1] || 0} entries
          </StatHelpText>
        </Stat>

        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Top City</StatLabel>
          <StatNumber fontSize="lg">
            {Object.entries(metrics.entriesByCity)
              .sort(([, a], [, b]) => (b as number) - (a as number))[0]?.[0] || 'N/A'}
          </StatNumber>
          <StatHelpText>
            {Object.entries(metrics.entriesByCity)
              .sort(([, a], [, b]) => (b as number) - (a as number))[0]?.[1] || 0} entries
          </StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* Charts */}
      <Grid templateColumns="repeat(auto-fit, minmax(400px, 1fr))" gap={6} mb={6}>
        {/* Growth Trend */}
        <Card>
          <CardHeader>
            <Heading size="sm">Growth Trend (Last 6 Months)</Heading>
          </CardHeader>
          <CardBody>
            {metrics.growthTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={metrics.growthTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#8884d8" name="Entries" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Text color="gray.500" textAlign="center" py={4}>
                No data available
              </Text>
            )}
          </CardBody>
        </Card>

        {/* State Distribution */}
        <Card>
          <CardHeader>
            <Heading size="sm">Top States</Heading>
          </CardHeader>
          <CardBody>
            {stateDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stateDistribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" name="Entries" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Text color="gray.500" textAlign="center" py={4}>
                No data available
              </Text>
            )}
          </CardBody>
        </Card>
      </Grid>

      {/* Main Content Tabs */}
      <Tabs lazyBehavior="keepMounted">
        <TabList>
          <Tab>
            <HStack>
              <FiMapIcon />
              <Text>Map View</Text>
            </HStack>
          </Tab>
          <Tab>
            <HStack>
              <FiUsersIcon />
              <Text>List View</Text>
            </HStack>
          </Tab>
        </TabList>

        <TabPanels>
          {/* Map Tab */}
          <TabPanel p={0} pt={4}>
            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
              <WaitingListStaticMap
                entries={filteredEntries}
                selectedEntry={selectedEntry}
                clusters={metrics.geographicClusters}
                showClusters={showClusters}
                onToggleView={setShowClusters}
              />
            </Box>
          </TabPanel>

          {/* List Tab */}
          <TabPanel p={0} pt={4}>
            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
              {/* Filters */}
              <HStack mb={4} spacing={4}>
                <InputGroup flex="1">
                  <InputLeftElement pointerEvents="none">
                    <FiSearchIcon color="gray.300" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search by address or reason..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
                <Select
                  placeholder="All States"
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  w="200px"
                >
                  {uniqueStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </Select>
              </HStack>

              {/* Table */}
              {filteredEntries.length > 0 ? (
                <Box
                  overflowX="auto"
                  overflowY="auto"
                  maxH="600px"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                >
                  <Table variant="simple" minW="100%">
                    <Thead>
                      <Tr>
                        <Th>Address</Th>
                        <Th>City</Th>
                        <Th>State</Th>
                        <Th>Zip Code</Th>
                        <Th>Date Added</Th>
                        <Th>Reason</Th>
                        <Th>Coordinates</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredEntries.map((entry) => (
                        <Tr
                          key={entry.id}
                          onClick={() => setSelectedEntry(entry)}
                          cursor="pointer"
                          bg={selectedEntry?.id === entry.id ? 'blue.50' : 'transparent'}
                          _hover={{ bg: 'gray.50' }}
                        >
                          <Td>{entry.street || 'N/A'}</Td>
                          <Td>{entry.city || 'N/A'}</Td>
                          <Td>{entry.state || 'N/A'}</Td>
                          <Td>{entry.zipCode || 'N/A'}</Td>
                          <Td>{formatDate(entry.createdAt)}</Td>
                          <Td>
                            <Badge colorScheme="orange" fontSize="xs">
                              {entry.reason || 'Out of service area'}
                            </Badge>
                          </Td>
                          <Td fontSize="xs">
                            {entry.latitude && entry.longitude
                              ? `${entry.latitude.toFixed(4)}, ${entry.longitude.toFixed(4)}`
                              : 'N/A'}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Text color="gray.500" textAlign="center" py={8}>
                  {searchTerm || stateFilter
                    ? 'No entries match your filters'
                    : 'No waiting list entries found'}
                </Text>
              )}
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default WaitingListManagement;

